import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState,
} from 'react';
import {
  Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import SportsEsportsOutlinedIcon from '@mui/icons-material/SportsEsportsOutlined';
import { getSpaceGameScoreboardLimits, updateSpaceGameScoreboardLimit } from '../../api/configApi';
import { C, btnBase, ctaGreen } from '../../theme/adminTokens';

// Same range as the API (SpaceGameRules) and the database check CK_space_game_scoreboard_max_rows.
export const MIN_SCOREBOARD_LIMIT = 1;
export const MAX_SCOREBOARD_LIMIT = 10000;

const RANGE_MESSAGE = `Enter a whole number from ${MIN_SCOREBOARD_LIMIT} to ${MAX_SCOREBOARD_LIMIT.toLocaleString('en-US')}, or leave it empty for no limit.`;

const toDraft = (value) => (value == null ? '' : String(value));

// '' = no limit (null); otherwise a whole number inside the range.
const parseDraft = (text) => {
  const t = (text ?? '').trim();
  if (t === '') return { valid: true, value: null };
  if (!/^\d+$/.test(t)) return { valid: false, value: null };
  const n = Number(t);
  return n >= MIN_SCOREBOARD_LIMIT && n <= MAX_SCOREBOARD_LIMIT ? { valid: true, value: n } : { valid: false, value: null };
};

// The admin portal is English-only: the en_us name, else the first one present, else the code.
const gameLabel = (game) => game?.localizedName?.values?.find((v) => v.localeId === 'en_us')?.value
  ?? game?.localizedName?.values?.[0]?.value
  ?? game?.gameCode
  ?? '';

/**
 * Global Admin "Games" tab of a space in the admin portal: one "max players on the scoreboard" field per game of the
 * space. The CMS Game Scoreboard of that game then lists only its top N players; empty = no limit. One Save for the
 * whole tab (like the Feature Flags tab); `saveAll` is exposed through the ref so the page's "unsaved changes" dialog
 * can save from there, and `onDirtyChange` tells the page whether there is anything unsaved.
 */
const SpaceGamesScoreboardPanel = forwardRef(function SpaceGamesScoreboardPanel({ spaceId, showToast, onDirtyChange }, ref) {
  const [games, setGames] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    getSpaceGameScoreboardLimits(spaceId)
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setGames(list);
        setDrafts(Object.fromEntries(list.map((g) => [g.gameId, toDraft(g.scoreboardMaxRows)])));
        setStatus('ready');
      })
      .catch(() => { if (!cancelled) setStatus('error'); });
    return () => { cancelled = true; };
  }, [spaceId, reloadKey]);

  const rows = useMemo(() => games.map((game) => {
    const parsed = parseDraft(drafts[game.gameId]);
    return { game, parsed, dirty: parsed.valid && parsed.value !== (game.scoreboardMaxRows ?? null) };
  }), [games, drafts]);
  const anyDirty = rows.some((r) => r.dirty);
  const anyInvalid = rows.some((r) => !r.parsed.valid);

  useEffect(() => { onDirtyChange?.(anyDirty); }, [anyDirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  // Saves every changed game, one request each, and keeps what the server answered. Stops at the first failure (the
  // games saved before it stay saved and show as such).
  const saveAll = useCallback(async () => {
    if (anyInvalid) return false;
    const changed = rows.filter((r) => r.dirty);
    if (changed.length === 0) return true;
    setSaving(true);
    try {
      for (const { game, parsed } of changed) {
        let saved;
        try {
          // eslint-disable-next-line no-await-in-loop
          saved = await updateSpaceGameScoreboardLimit(spaceId, game.gameId, parsed.value);
        } catch (_) {
          showToast?.(`Failed to save the scoreboard limit for ${gameLabel(game)}`, 'error');
          return false;
        }
        const limit = saved?.scoreboardMaxRows ?? null;
        setGames((prev) => prev.map((g) => (g.gameId === game.gameId ? { ...g, scoreboardMaxRows: limit } : g)));
        setDrafts((prev) => ({ ...prev, [game.gameId]: toDraft(limit) }));
      }
      showToast?.('Scoreboard limits saved');
      return true;
    } finally {
      setSaving(false);
    }
  }, [rows, anyInvalid, spaceId, showToast]);

  useImperativeHandle(ref, () => ({ saveAll }), [saveAll]);

  return (
    <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${C.border}`, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SportsEsportsOutlinedIcon sx={{ fontSize: 18, color: C.muted }} />
          <Typography variant="caption" sx={{ fontWeight: 800, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Game Settings
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          onClick={saveAll}
          disabled={status !== 'ready' || !anyDirty || anyInvalid || saving}
          sx={{ ...btnBase, ...ctaGreen }}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Box>
      <Typography variant="body2" sx={{ color: C.muted, mb: 2.5 }}>
        Max players on the scoreboard: the Game Scoreboard of this space lists only the top N players of the game
        (their real ranks 1 to N; search and pages work inside that list). Every game starts at 100: change the number, or
        leave a field empty for no limit. Announcement winners and the in-game leaderboards are not affected.
      </Typography>

      {status === 'loading' && (
        <Box sx={{ textAlign: 'center', py: 4, color: C.muted }}>
          <Typography variant="body2" role="status">Loading games…</Typography>
        </Box>
      )}

      {status === 'error' && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" role="alert" sx={{ color: '#ef4444', mb: 1.5 }}>Could not load the games of this space.</Typography>
          <Button size="small" variant="outlined" onClick={() => setReloadKey((k) => k + 1)} sx={{ ...btnBase, fontWeight: 600 }}>Retry</Button>
        </Box>
      )}

      {status === 'ready' && games.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4, color: C.muted }}>
          <Typography variant="body2">This space has no games yet.</Typography>
        </Box>
      )}

      {status === 'ready' && games.length > 0 && (
        <TableContainer sx={{ border: `1px solid ${C.border}`, borderRadius: '8px' }}>
          <Table size="small" aria-label="Games of this space">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                <TableCell sx={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Game</TableCell>
                <TableCell sx={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.04em', width: { sm: 360 } }}>
                  Max players on the scoreboard
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(({ game, parsed }) => (
                <TableRow key={game.gameId}>
                  <TableCell sx={{ fontSize: 13, fontWeight: 600, color: '#1e293b', verticalAlign: 'top', pt: 2 }}>
                    {gameLabel(game)}
                    {game.isActive === false && (
                      <Typography component="span" variant="caption" sx={{ ml: 1, color: C.muted, fontWeight: 500 }}>(switched off)</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      fullWidth
                      value={drafts[game.gameId] ?? ''}
                      onChange={(e) => setDrafts((prev) => ({ ...prev, [game.gameId]: e.target.value }))}
                      placeholder="No limit"
                      error={!parsed.valid}
                      helperText={parsed.valid ? ' ' : RANGE_MESSAGE}
                      disabled={saving}
                      inputProps={{ min: MIN_SCOREBOARD_LIMIT, max: MAX_SCOREBOARD_LIMIT, 'aria-label': `Max players on the scoreboard for ${gameLabel(game)}` }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
});

export default SpaceGamesScoreboardPanel;
